import React from "react";
import styled, { css } from "styled-components";
import { ISearchItem } from "../../../Types";
import { Button, Heading, ResponsiveImage } from "../..";
import CMSIcon from "../../CMS/CMSIcon";

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  margin: 0 auto;
  padding: 0;
  justify-content: center;
`;

const ListItem = styled.li`
  flex-basis: 100%;
  flex-grow: 0;
  flex-shrink: 0;
  margin: 0;
  padding: 10px;
  text-align: center;

  @media screen and (min-width: 600px) {
    flex-basis: 50%;
  }
  @media screen and (min-width: 869px) {
    flex-basis: 33.333%;
  }
  @media screen and (min-width: 1139px) {
    flex-basis: 25%;
  }
`;

const GridIcon = styled.div`
  align-items: center;
  background-color: ${(props) => props.theme.colors.neoMint};
  border-radius: 100%;
  color: white;
  display: flex;
  height: 33px;
  justify-content: center;
  margin: 0 auto;
  width: 33px;

  ${(props) => props.theme.breakpoints("xsm")`
      opacity: 0;
      transform: translateY(9px);
      transition: all 0.3s ease-in;
    `}

  svg {
    display: inline-block;
    height: 13px;
    width: 13px;
  }
`;

const TileGridItem = styled.div`
  background-color: #fff;
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.43);
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 0 9px;
`;

const NameInfo = styled.p`
  font-size: 15px;
  height: 36px;
  font-weight: 600;
  margin: 0 auto;
  padding: 0 15px 0;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const BorderBottom = styled.div`
  border-bottom: 1px solid;
  color: ${(props) => props.theme.colors.neoMint};
  margin: 15px 15px 0;
`;

const ThemeInfo = styled.ul`
  display: flex;
  list-style: none;
  margin: 10px auto;
  min-width: calc(100% - 14px);
  padding: 0;

  svg {
    display: block;
    height: auto;
    margin: 0 auto;
    width: 30px;
  }
`;

const ThemeInfoItem = styled.li`
  flex-basis: 16.6666%;
  font-size: 8px;
  font-weight: 600;
  margin: 0;
  padding: 0;
  opacity: 0.23;

  ${(props) => {
    if (props.isActive) {
      return `
        opacity: 1;
        `;
    }
  }}

  ${(props) => {
    switch (props.tileTheme) {
      case "energy":
        return css`
          svg {
            color: ${props.theme.colors.lilla2};
          }
        `;
      case "food":
        return css`
          svg {
            color: ${props.theme.colors.solGul};
          }
        `;
      case "climate":
        return css`
          svg {
            color: ${props.theme.colors.lyseBlaa};
          }
        `;
      case "ressources":
        return css`
          svg {
            color: ${props.theme.colors.koral2};
          }
        `;
      case "nature":
        return css`
          svg {
            color: ${props.theme.colors.koral};
          }
        `;
      case "health":
        return css`
          svg {
            color: ${props.theme.colors.lilla};
          }
        `;
    }
  }}
`;

const GridImage = styled.div`
  display: flex;
  flex-direction: column;
  height: 33px;
  justify-content: center;
  min-height: 100px;

  img {
    display: block;
    margin: 10px auto;
    max-height: 61px;
  }
`;

const GridHeader = styled.div`
  display: grid;
  padding: 10px 20px;
  height: 100%;
  place-items: center;

  a {
    margin-top: 15px;
    align-self: end;
    padding-inline: 15px;
  }
  a:nth-child(3) {
 align-self:start;

  h2 {
    display: block;
    margin-bottom: 0 !important;
    text-align: center;
    hyphens: auto;
  }
`;

const CtaContainer = styled.div`
  display: grid;
  align-self: end;
  width: 100%;
  justify-content: center;
`;

const EmptyLinkContainer = styled.span`
  margin-top: 15px;
  height: 26.8px;
  max-width: 200px;
`;

export interface ITileGrid {
  Data: Array<ISearchItem>;
  Themes: any;
}

const TileGrid: React.FC<ITileGrid> = ({ Data, Themes: ThemeData }) => {
  return (
    <List>
      {Data.map(
        (
          {
            Url,
            Header,
            SchoolYears,
            Logo,
            DendogramHostName,
            Themes,
            SecondLinkUrl,
            SecondLinkName,
          },
          key
        ) => {
          return (
            <ListItem key={`ListItem_${key}`}>
              <TileGridItem>
                <GridImage>
                  <ResponsiveImage {...Logo} />
                  {/*{HostData.map(({ Logo }, index) => (*/}

                  {/*))}.*/}
                </GridImage>
                <NameInfo>{DendogramHostName}</NameInfo>
                <BorderBottom />
                <ThemeInfo>
                  {ThemeData.map(({ ThemeId, ThemeName }, index) => (
                    <ThemeInfoItem
                      key={`theme_${index}`}
                      isActive={Themes.includes(ThemeId)}
                      tileTheme={ThemeId}
                    >
                      <CMSIcon icon={"tilelistthemeicon"} />
                      {ThemeName}
                    </ThemeInfoItem>
                  ))}
                </ThemeInfo>
                <GridHeader>
                  <div>
                    <Heading noMargin size={300}>
                      {Header}
                    </Heading>
                    <Heading size={300}>{`(${SchoolYears})`}</Heading>
                  </div>
                  <CtaContainer>
                    <Button
                      component="a"
                      href={Url}
                      variant="CTACards"
                      target="_blank"
                    >
                      <span className="label">Gå til forløb</span>
                      <CMSIcon icon={"targetblank"} />
                    </Button>
                    {SecondLinkName && SecondLinkUrl ? (
                      <Button
                        component="a"
                        href={SecondLinkUrl}
                        variant="Link"
                        download
                      >
                        <span className="label">{SecondLinkName}</span>
                        <CMSIcon icon="document" />
                      </Button>
                    ) : (
                      <EmptyLinkContainer />
                    )}
                  </CtaContainer>
                </GridHeader>
              </TileGridItem>
            </ListItem>
          );
        }
      )}
    </List>
  );
};

export default TileGrid;
